import type { ReactElement } from 'react';
import { useSearchParams } from 'react-router';
import { useExhibitionsQuery } from '@/entities/exhibition';
import { EXHIBITION_PURPOSE_PARAM } from '@/shared/config';
import { Button, ComingSoonNotice, SitePageShell } from '@/shared/ui';
import { ExhibitionCardGrid } from '@/widgets/exhibition-card-grid';
import { ExhibitionPurposeTabs } from '@/widgets/exhibition-purpose-tabs';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

/**
 * 기획전(/exhibition) — 목적 탭과 카드. 선택한 탭은 URL 파라미터(EXHIBITION_PURPOSE_PARAM)가 단일 원천이다.
 * exhibitions.list 한 번으로 탭과 카드를 모두 받는다 — 탭을 바꿔도 다시 요청하지 않는다.
 * 스토어와 같은 디자인 언어 — 큰 "Exhibition" 제목·설명, 밑줄 목적 탭, 각진 회색 사진 칸의 편집형 카드.
 */
export const ExhibitionPage = (): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const exhibitionsQuery = useExhibitionsQuery();
  const purposes = exhibitionsQuery.data?.purposes ?? [];

  // 없거나 모르는 id 면 첫 번째 탭 — 공유된 링크가 깨지지 않게 조용히 떨어뜨린다.
  const requestedId = searchParams.get(EXHIBITION_PURPOSE_PARAM);
  const selected = purposes.find((purpose) => purpose.id === requestedId) ?? purposes[0];

  const selectPurpose = (purposeId: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set(EXHIBITION_PURPOSE_PARAM, purposeId);
    // replace — 상품 목록에서 뒤로 오면 보던 탭으로 돌아오고, 탭 기록은 쌓지 않는다.
    setSearchParams(next, { replace: true });
  };

  // 데이터가 있는데 백그라운드 갱신만 실패한 경우는 전면 오류가 아니다 — 보던 목록을 그대로 둔다.
  const isFirstLoadError = exhibitionsQuery.isError && exhibitionsQuery.data === undefined;

  // 목적이 0개이거나 모든 목적의 카드 합이 0개면 탭 없이 화면 전체가 준비중이다(확정 규칙).
  const cardCount = purposes.reduce((total, purpose) => total + purpose.exhibitions.length, 0);
  const isComingSoon = !exhibitionsQuery.isPending && !isFirstLoadError && cardCount === 0;

  if (isComingSoon) {
    return (
      <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
        <ComingSoonNotice title="기획전" />
      </SitePageShell>
    );
  }

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />} className="pt-12 sm:pt-16">
      <header className="flex flex-col gap-4">
        {/* 보이는 제목은 영문 "Exhibition" 한 단어, 보조기기에는 한국어 화면 이름을 함께 읽힌다. */}
        <h1 className="text-[clamp(2.5rem,5vw,4.5rem)] leading-none font-bold tracking-[-0.03em]">
          <span aria-hidden>Exhibition</span>
          <span className="sr-only">기획전</span>
        </h1>
        <p className="text-[15px] text-muted-foreground sm:text-base">
          목적에 맞게 묶은 상품을 한 번에 살펴보세요.
        </p>
      </header>

      {isFirstLoadError ? (
        // 원문 오류는 화면에 싣지 않는다 — 사용자용 문장과 재시도만 남긴다.
        <div
          role="alert"
          className="mt-10 flex flex-col items-center gap-4 border-y border-foreground/10 py-16 text-center sm:mt-14"
        >
          <p className="text-sm text-destructive">기획전을 불러오지 못했습니다.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void exhibitionsQuery.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      ) : (
        <div className="mt-10 space-y-10 sm:mt-14">
          {selected !== undefined && (
            <ExhibitionPurposeTabs
              purposes={purposes}
              selectedId={selected.id}
              onSelect={selectPurpose}
            />
          )}
          <ExhibitionCardGrid
            exhibitions={selected?.exhibitions ?? []}
            isPending={exhibitionsQuery.isPending}
          />
        </div>
      )}
    </SitePageShell>
  );
};
